import React, { useState } from 'react';

import { useReduxHelpTopic } from './helpTopicHooks';

/** Minimal shape of a click_thru_template needed by this hook. */
export interface ClickThruLink {
	text: string;
	help_tag_name: string;
	not_found_message: string;
}

export interface ClickThruSection {
	type: string;
	id: string;
	label: string;
	variant?: string;
	link?: ClickThruLink[];
}

export interface ClickThruTemplate {
	Sections: ClickThruSection[];
}

interface UseClickThruLabelResult {
	/**
	 * Given a section label string, returns the label as a ReactNode.
	 * If the clickThruTemplate has a link whose text appears in the label,
	 * that text is wrapped in an <a> tag linking to the help topic file.
	 * If no help topic is found, clicking the link opens a notice modal.
	 * If there is no clickThruLink at all, the plain label string is returned.
	 */
	renderLabelWithLink: (label: string) => React.ReactNode;
	/** Set to the not_found_message string when the link is clicked but no help topic exists. Null otherwise. */
	notFoundMessage: string | null;
	/** Call this to dismiss the not-found notice (e.g. when the user clicks OK or closes the modal). */
	clearNotFoundMessage: () => void;
}

export const useClickThruLabel = (
	clickThruTemplate: ClickThruTemplate | undefined,
): UseClickThruLabelResult => {
	const [notFoundMessage, setNotFoundMessage] = useState<string | null>(null);

	const clickThruLink = clickThruTemplate?.Sections?.flatMap((s) => s.link ?? []).find(Boolean);
	const helpTopic = useReduxHelpTopic(clickThruLink?.help_tag_name ?? '');

	const renderLabelWithLink = (label: string): React.ReactNode => {
		// No link configured — just return the plain label
		if (!clickThruLink) return label;

		const { text: linkText, not_found_message } = clickThruLink;
		const idx = label.indexOf(linkText);

		// Link text not found in label — just return the plain label
		if (idx === -1) return label;

		const before = label.slice(0, idx);
		const after = label.slice(idx + linkText.length);

		let linkElement: React.ReactNode;
		if (helpTopic) {
			const href = `https://${global.config.apiHost}${global.config.apiBasePath}/pdb/help/${helpTopic.help_topic_id}/file`;
			linkElement = (
				<a href={href} target="_blank" rel="noreferrer">
					{linkText}
				</a>
			);
		} else {
			linkElement = (
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						setNotFoundMessage(not_found_message);
					}}
				>
					{linkText}
				</a>
			);
		}

		return (
			<>
				{before}
				{linkElement}
				{after}
			</>
		);
	};

	return {
		renderLabelWithLink,
		notFoundMessage,
		clearNotFoundMessage: () => setNotFoundMessage(null),
	};
};
